import * as Yup from 'yup';
import { DetailMode } from '@eas/common-web';
import { icoValidation } from '@composite/contact/contact-schema';
import { Contact, Subject } from '@models';
import { SubjectType } from '@enums';

export function useValidationSchema() {
  return Yup.object<Subject>().shape(subjectSchema);
}

export function useSubjectToolbarValidationSchema({
  participantMode,
  mode,
}: {
  participantMode: boolean;
  mode: DetailMode;
}) {
  return Yup.object<Subject>().shape({
    selectedContact: Yup.mixed<Contact>()
      .test('', '', function (value) {
        const { createError, parent } = this;

        if (
          participantMode &&
          mode === DetailMode.NEW &&
          (parent.contacts?.length ?? 0) === 0
        ) {
          return true;
        }

        if (!value) {
          return createError({
            path: 'contacts',
            message: 'Musí být vybrán jeden kontakt.',
          });
        }
        return true;
      })
      .nullable(),
    ...subjectSchema,
  }) as Yup.Schema<Subject>;
}

const subjectSchema = {
  type: Yup.mixed<SubjectType>().nullable().required('Musí být vyplněné'),
  firstName: Yup.string()
    .nullable()
    .test('', '', function (value) {
      const { createError, path, parent } = this;

      if (
        !value &&
        [
          SubjectType.NATURAL_PERSON,
          SubjectType.BUSINESS_NATURAL_PERSON,
        ].includes(parent.type) &&
        !parent.lastName
      ) {
        return createError({
          path,
          message: 'Musí být vyplněno jméno nebo příjmení',
        });
      }

      if (
        !value &&
        parent.type === SubjectType.UNSPECIFIED &&
        !parent.companyName &&
        !parent.lastName
      ) {
        return createError({
          path,
          message: 'Musí být vyplněné, pokud není vyplněno příjmení ani název',
        });
      }

      return true;
    }),
  birthDate: Yup.string()
    .nullable()
    .test('', '', function (value) {
      const { createError, path } = this;

      if (value && new Date(value) > new Date()) {
        return createError({ path, message: 'Datum nesmí být v budoucnosti' });
      }

      return true;
    }),
  lastName: Yup.string()
    .nullable()
    .test('', '', function (value) {
      const { createError, path, parent } = this;

      if (
        !value &&
        [
          SubjectType.NATURAL_PERSON,
          SubjectType.BUSINESS_NATURAL_PERSON,
        ].includes(parent.type) &&
        !parent.firstName
      ) {
        return createError({
          path,
          message: 'Musí být vyplněno jméno nebo příjmení',
        });
      }

      if (
        !value &&
        parent.type === SubjectType.UNSPECIFIED &&
        !parent.companyName &&
        !parent.firstName
      ) {
        return createError({
          path,
          message: 'Musí být vyplněné, pokud není vyplněno jméno ani název',
        });
      }

      return true;
    }),
  contacts: Yup.array<Contact>()
    .transform((value) => value ?? undefined)
    .test('', '', function (value) {
      const { createError, path, parent } = this;

      if (!parent?.id || (value?.length ?? 0) > 0) {
        return true;
      }

      return createError({ path, message: 'Povinné pole' });
    }),
  ico: Yup.string().when('type', {
    is: (val) => val !== SubjectType.NATURAL_PERSON,

    then: icoValidation.notRequired(),
    otherwise: Yup.string().notRequired(),
  }),
  companyName: Yup.string()
    .nullable()
    .test('', '', function (value) {
      const { createError, path, parent } = this;

      if (!value && [SubjectType.LEGAL_ENTITY].includes(parent.type)) {
        return createError({
          path,
          message: 'Musí být vyplněné',
        });
      }

      if (
        !value &&
        parent.type === SubjectType.UNSPECIFIED &&
        !parent.lastName &&
        !parent.firstName
      ) {
        return createError({
          path,
          message: 'Musí být vyplněné, pokud není vyplněno jméno ani příjmení',
        });
      }

      return true;
    }),
};
